const DRAG_THRESHOLD_PX = 5;
const LINE_Y = 130;
const SCROLL_DEBOUNCE_MS = 100;
const REENABLE_AFTER_MS = 300;

class SubcollectionAnchors extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.all-collections-section-subcollections');
    this.anchors = this.querySelectorAll('a.all-collections-section-subcollection');
    this.sectionToAnchor = new Map();
    this.observer = null;
    this._scrollT = null;
    this._reenableT = null;
    this._observerPaused = false;
    this._pointer = { x: 0, y: 0 };
    this._scrollStart = 0;
    this._moved = false;
    this._suppressClick = false;
    this._onScroll = () => this._debouncedUpdate();
    this._onMove = this._move.bind(this);
    this._onEnd = this._end.bind(this);
    this._onClick = this._click.bind(this);
    this._onDragStart = (e) => { e.preventDefault(); e.dataTransfer?.setData('text/plain', ''); };
  }

  connectedCallback() {
    if (!this.slider) return;
    this.slider.querySelectorAll('a, button').forEach((el) => el.setAttribute('draggable', 'false'));
    this.slider.addEventListener('dragstart', this._onDragStart, true);
    this.slider.addEventListener('mousedown', this._start.bind(this));
    this.slider.addEventListener('touchstart', this._start.bind(this), { passive: true });
    this._buildSectionMap();
    this._setupObserver();
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  disconnectedCallback() {
    this._removePointerListeners();
    this.observer?.disconnect();
    window.removeEventListener('scroll', this._onScroll);
    clearTimeout(this._scrollT);
    clearTimeout(this._reenableT);
    this.slider?.removeEventListener('dragstart', this._onDragStart, true);
    this.slider?.removeEventListener('click', this._onClick, true);
  }

  _buildSectionMap() {
    this.sectionToAnchor.clear();
    this.anchors.forEach((a) => {
      const id = (a.getAttribute('href') || '').replace(/^#/, '');
      const el = id && document.getElementById(id);
      if (el) this.sectionToAnchor.set(el, a);
    });
  }

  _setupObserver() {
    const sections = [...this.sectionToAnchor.keys()];
    if (!sections.length) return;
    this.observer = new IntersectionObserver(
      () => this._updateFromViewport(),
      { root: null, rootMargin: `${LINE_Y}px 0px -100% 0px`, threshold: 0 }
    );
    sections.forEach((el) => this.observer.observe(el));
  }

  _debouncedUpdate() {
    clearTimeout(this._scrollT);
    this._scrollT = setTimeout(() => { this._scrollT = null; this._updateFromViewport(); }, SCROLL_DEBOUNCE_MS);
  }

  _updateFromViewport() {
    if (this._observerPaused) {
      clearTimeout(this._reenableT);
      this._reenableT = setTimeout(() => { this._observerPaused = false; this._reenableT = null; }, REENABLE_AFTER_MS);
      return;
    }
    const sections = [...this.sectionToAnchor.keys()];
    const rects = sections.map((s) => s.getBoundingClientRect());
    const allAbove = rects.every((r) => r.top > LINE_Y);
    if (allAbove && this.anchors.length) {
      if (this._activeAnchor() !== this.anchors[0]) this.setActiveAnchor(this.anchors[0]);
      return;
    }
    let best = null;
    let bestTop = -Infinity;
    let bestContaining = null;
    let bestContainingTop = -Infinity;
    sections.forEach((section, i) => {
      const r = rects[i];
      if (r.top > LINE_Y) return;
      if (r.bottom >= LINE_Y && r.top > bestContainingTop) { bestContainingTop = r.top; bestContaining = section; }
      if (r.top > bestTop) { bestTop = r.top; best = section; }
    });
    const chosen = bestContaining ?? best;
    const anchor = chosen ? this.sectionToAnchor.get(chosen) : this.anchors[0];
    if (anchor && this._activeAnchor() !== anchor) this.setActiveAnchor(anchor);
  }

  _activeAnchor() {
    return this.slider?.querySelector('a.all-collections-section-subcollection.active');
  }

  setActiveAnchor(anchor) {
    if (!anchor || !this.slider) return;
    this.anchors.forEach((a) => a.classList.remove('active'));
    anchor.classList.add('active');
    anchor.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }

  _start(e) {
    const p = e.touches?.[0] ?? e;
    this._pointer = { x: p.clientX, y: p.clientY };
    this._scrollStart = this.slider.scrollLeft;
    this._moved = false;
    if (e.touches) {
      window.addEventListener('touchmove', this._onMove, { passive: false });
      window.addEventListener('touchend', this._onEnd, { passive: true });
      window.addEventListener('touchcancel', this._onEnd, { passive: true });
    } else {
      window.addEventListener('mousemove', this._onMove);
      window.addEventListener('mouseup', this._onEnd);
    }
    this.slider.addEventListener('click', this._onClick, true);
  }

  _move(e) {
    const p = e.touches?.[0] ?? e;
    const dx = p.clientX - this._pointer.x;
    const dy = p.clientY - this._pointer.y;
    if (!this._moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) this._moved = true;
    if (this._moved) {
      if (e.touches) e.preventDefault();
      this.slider.scrollLeft = this._scrollStart - dx;
    }
  }

  _end() {
    this._removePointerListeners();
    this._suppressClick = this._moved;
    setTimeout(() => {
      this.slider.removeEventListener('click', this._onClick, true);
      this._suppressClick = false;
    }, 350);
  }

  _removePointerListeners() {
    window.removeEventListener('mousemove', this._onMove);
    window.removeEventListener('mouseup', this._onEnd);
    window.removeEventListener('touchmove', this._onMove);
    window.removeEventListener('touchend', this._onEnd);
    window.removeEventListener('touchcancel', this._onEnd);
  }

  _click(e) {
    this.slider.removeEventListener('click', this._onClick, true);
    if (this._suppressClick) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    } else {
      const anchor = e.target.closest('a.all-collections-section-subcollection');
      if (anchor) { this._observerPaused = true; this.setActiveAnchor(anchor); }
    }
  }
}

if (!customElements.get('subcollection-anchors')) {
  customElements.define('subcollection-anchors', SubcollectionAnchors);
}
